import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtUGX(n: number) {
  return 'UGX ' + Math.round(n).toLocaleString('en-UG');
}

function fmtShort(n: number) {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(Math.round(n));
}

function Skeleton({ h = 96 }: { h?: number }) {
  return (
    <div style={{
      height: h, borderRadius: 10,
      background: 'var(--surface2)', animation: 'pulse 1.5s infinite',
    }} />
  );
}

interface PLReport {
  revenue: { grossRevenue: string; netRevenue: string; vat: string; ordersCount: number };
  cogs: { totalCogs: string };
  grossProfit: { amount: string; marginPct: string };
  operatingExpenses: { total: string };
  ebitda: { amount: string; marginPct: string };
}

interface CashFlowReport {
  inflows: { total: string; byMethod: Record<string, string> };
  outflows: { expenses: string; pettyNet: string; total: string };
  netCashFlow: string;
}

interface VATReturn {
  period: { month: string };
  outputVat: { totalSalesInclVat: string; vatCollected: string };
  inputVat: { vatReclaimed: string };
  netVatPayable: string;
  status: string;
}

interface Expense {
  id: string;
  description: string;
  amountUgx: string;
  status: string;
  date: string;
  account?: { name: string };
  supplier?: { name: string };
}

function ChartTooltipContent({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
  if (!active || !payload?.length || !payload[0]) return null;
  return (
    <div className="card" style={{ padding: '8px 12px', fontSize: 12 }}>
      <p style={{ color: 'var(--text-3)', marginBottom: 2 }}>{label}</p>
      <p style={{ fontWeight: 600, color: 'var(--text)' }}>{fmtUGX(payload[0].value)}</p>
    </div>
  );
}

const TICK = { fontSize: 11, fill: 'var(--text-3)' };
const GRID = { strokeDasharray: '3 3', stroke: 'var(--border)' };

export function FinancePage() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = today.toISOString().slice(0, 10);

  const [activeTab, setActiveTab] = useState<'pl' | 'cashflow' | 'vat' | 'expenses'>('pl');
  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(lastDay);
  const [vatMonth, setVatMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'));
  const [vatYear, setVatYear] = useState(String(today.getFullYear()));

  const { data: pl, isLoading: plLoading } = useQuery<PLReport>({
    queryKey: ['pl', from, to],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/finance/reports/pl?from=${from}&to=${to}`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data;
    },
  });

  const { data: cashFlow, isLoading: cfLoading } = useQuery<CashFlowReport>({
    queryKey: ['cashflow', from, to],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/finance/reports/cash-flow?from=${from}&to=${to}`, { headers: authHeaders() });
      const j = await res.json();
      return j.data;
    },
    enabled: activeTab === 'cashflow',
  });

  const { data: vat, isLoading: vatLoading } = useQuery<VATReturn>({
    queryKey: ['vat', vatMonth, vatYear],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/finance/reports/vat-return?periodMonth=${vatMonth}&periodYear=${vatYear}`, { headers: authHeaders() });
      const j = await res.json();
      return j.data;
    },
    enabled: activeTab === 'vat',
  });

  const { data: expenses = [], isLoading: expLoading } = useQuery<Expense[]>({
    queryKey: ['expenses', from, to],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/finance/expenses?dateFrom=${from}&dateTo=${to}`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
    enabled: activeTab === 'expenses',
  });

  const expensesByDay = expenses.reduce<Record<string, number>>((acc, e) => {
    const d = e.date.slice(0, 10);
    acc[d] = (acc[d] ?? 0) + Number(e.amountUgx);
    return acc;
  }, {});
  const expenseChartData = Object.entries(expensesByDay).sort().map(([date, total]) => ({
    date: new Date(date).toLocaleDateString('en-UG', { day: 'numeric', month: 'short' }),
    total,
  }));

  return (
    <div>
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>P&L, cash flow, VAT returns and expense tracking</p>
        </div>
        {activeTab !== 'vat' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--text-3)' }}>From</span>
            <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 140 }} />
            <span style={{ color: 'var(--text-3)' }}>To</span>
            <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ width: 140 }} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {(['pl', 'cashflow', 'vat', 'expenses'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              background: activeTab === tab ? 'var(--surface)' : 'transparent',
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              border: 'none', cursor: 'pointer',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {tab === 'pl' ? 'P&L' : tab === 'cashflow' ? 'Cash Flow' : tab === 'vat' ? 'VAT Return' : 'Expenses'}
          </button>
        ))}
      </div>

      {/* P&L */}
      {activeTab === 'pl' && (
        plLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} />)}
          </div>
        ) : !pl ? (
          <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No P&L data for this period.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              {[
                { label: 'Net Revenue', value: fmtUGX(Number(pl.revenue?.netRevenue ?? 0)), positive: true },
                { label: 'Gross Profit', value: fmtUGX(Number(pl.grossProfit?.amount ?? 0)), positive: Number(pl.grossProfit?.amount ?? 0) >= 0 },
                { label: 'EBITDA', value: fmtUGX(Number(pl.ebitda?.amount ?? 0)), positive: Number(pl.ebitda?.amount ?? 0) >= 0 },
              ].map(kpi => (
                <div key={kpi.label} className="stat-card">
                  <p className="stat-label">{kpi.label}</p>
                  <p className="stat-value" style={{ color: kpi.positive ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--font-mono)' }}>{kpi.value}</p>
                </div>
              ))}
            </div>
            <div className="card" style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: 'Gross Revenue', value: Number(pl.revenue?.grossRevenue ?? 0) },
                { label: 'VAT', value: Number(pl.revenue?.vat ?? 0) },
                { label: 'Net Revenue', value: Number(pl.revenue?.netRevenue ?? 0) },
                { label: 'Cost of Goods Sold', value: Number(pl.cogs?.totalCogs ?? 0) },
                { label: 'Gross Profit', value: Number(pl.grossProfit?.amount ?? 0) },
                { label: 'Operating Expenses', value: Number(pl.operatingExpenses?.total ?? 0) },
                { label: 'EBITDA', value: Number(pl.ebitda?.amount ?? 0) },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0', borderBottom: i < 6 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ color: 'var(--text-2)' }}>{row.label}</span>
                  <span style={{ fontWeight: 500, color: row.value < 0 ? 'var(--red)' : 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Math.abs(row.value))}</span>
                </div>
              ))}
            </div>
            <div className="card">
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Margins</p>
              {[
                { label: 'Gross Margin', value: pl.grossProfit?.marginPct ?? '0.0%' },
                { label: 'EBITDA Margin', value: pl.ebitda?.marginPct ?? '0.0%' },
                { label: 'Orders', value: String(pl.revenue?.ordersCount ?? 0) },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-2)' }}>{item.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Cash Flow */}
      {activeTab === 'cashflow' && (
        cfLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} />)}
          </div>
        ) : !cashFlow ? (
          <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No cash flow data.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div className="card">
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Inflows</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(cashFlow.inflows?.byMethod ?? {}).map(([method, amt]) => (
                  <div key={method} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-2)', textTransform: 'capitalize' }}>{method.replace(/_/g, ' ')}</span>
                    <span style={{ fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(amt))}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                  <span style={{ color: 'var(--text)' }}>Total Inflows</span>
                  <span style={{ color: 'var(--green)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(cashFlow.inflows?.total ?? 0))}</span>
                </div>
              </div>
            </div>
            <div className="card">
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Outflows</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-2)' }}>Expenses</span>
                  <span style={{ fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(cashFlow.outflows?.expenses ?? 0))}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-2)' }}>Petty Cash (net)</span>
                  <span style={{ fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(cashFlow.outflows?.pettyNet ?? 0))}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                  <span style={{ color: 'var(--text)' }}>Total Outflows</span>
                  <span style={{ color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(cashFlow.outflows?.total ?? 0))}</span>
                </div>
              </div>
            </div>
            <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Net Cash Flow</p>
              <p style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-mono)', color: Number(cashFlow.netCashFlow ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {fmtUGX(Number(cashFlow.netCashFlow ?? 0))}
              </p>
            </div>
          </div>
        )
      )}

      {/* VAT Return */}
      {activeTab === 'vat' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
            <div className="field" style={{ width: 160 }}>
              <label className="field-label">Month</label>
              <select className="input" value={vatMonth} onChange={e => setVatMonth(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => ({ v: String(i + 1).padStart(2, '0'), l: new Date(2000, i).toLocaleString('default', { month: 'long' }) })).map(m => (
                  <option key={m.v} value={m.v}>{m.l}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 100 }}>
              <label className="field-label">Year</label>
              <input className="input" type="number" value={vatYear} onChange={e => setVatYear(e.target.value)} />
            </div>
          </div>
          {vatLoading ? (
            <Skeleton h={260} />
          ) : !vat ? (
            <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No VAT data for this period.</div>
          ) : (
            <div className="card" style={{ maxWidth: 520 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 20 }}>VAT Return — {vat.period?.month}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '14px 16px' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Output Tax (Sales)</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                    <span style={{ color: 'var(--text-2)' }}>Total Sales (incl. VAT)</span>
                    <span style={{ fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(vat.outputVat?.totalSalesInclVat ?? 0))}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <span style={{ color: 'var(--text)' }}>VAT Collected (18%)</span>
                    <span style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(vat.outputVat?.vatCollected ?? 0))}</span>
                  </div>
                </div>
                <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '14px 16px' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Input Tax (Purchases)</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <span style={{ color: 'var(--text)' }}>VAT Reclaimed</span>
                    <span style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(vat.inputVat?.vatReclaimed ?? 0))}</span>
                  </div>
                </div>
                <div style={{
                  borderRadius: 10, padding: '14px 16px',
                  background: vat.status === 'payable' ? 'var(--amber-bg)' : 'var(--green-bg)',
                  border: `1px solid ${vat.status === 'payable' ? 'var(--amber)' : 'var(--green)'}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                    Net VAT {vat.status === 'payable' ? 'Payable to URA' : 'Refund Due'}
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: vat.status === 'payable' ? 'var(--amber)' : 'var(--green)', fontFamily: 'var(--font-mono)' }}>
                    {fmtUGX(Math.abs(Number(vat.netVatPayable ?? 0)))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expenses */}
      {activeTab === 'expenses' && (
        expLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={48} />)}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {expenseChartData.length > 0 && (
              <div className="card">
                <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>Daily Expenses</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={expenseChartData} barSize={24}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="date" tick={TICK} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtShort} tick={TICK} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="total" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {expenses.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No expenses for this period.</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Account</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map(exp => (
                        <tr key={exp.id}>
                          <td style={{ fontWeight: 500, color: 'var(--text)' }}>{exp.description}</td>
                          <td style={{ color: 'var(--text-2)' }}>{exp.account?.name ?? '—'}</td>
                          <td style={{ color: 'var(--text-2)' }}>{exp.supplier?.name ?? '—'}</td>
                          <td style={{ color: 'var(--text-2)' }}>{new Date(exp.date).toLocaleDateString()}</td>
                          <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(Number(exp.amountUgx))}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`badge ${exp.status === 'APPROVED' ? 'badge-green' : exp.status === 'PENDING' ? 'badge-amber' : 'badge-gray'}`}>
                              {exp.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
}
